import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowUpRight } from 'lucide-react';
import { AGENTS, AGENT_TEMPLATES, PORTCOS } from '../mock/platformData';
import { rcmApi } from '../api/rcmApi';
import { API_BASE, apiFetch } from '../api/base';
import { usePortCo } from '../context/PortCoContext';
import { AgentSwitchPanel } from '../components/AgentSwitchPanel';
import type { AgentStatus } from '../types';

const getStatusBadge = (status: AgentStatus) => {
  switch (status) {
    case 'active':
      return { bg: 'bg-mantis-50 text-mantis-600 border-mantis-200', dot: 'bg-mantis-500', label: 'Active' };
    case 'processing':
      return { bg: 'bg-redwood-50 text-redwood-500 border-redwood-200', dot: 'bg-redwood-500', label: 'Processing' };
    case 'error':
      return { bg: 'bg-rose-50 text-rose-600 border-rose-200', dot: 'bg-rose-500', label: 'Error' };
    case 'idle':
    case 'paused':
    default:
      return { bg: 'bg-amber-50 text-amber-600 border-amber-200', dot: 'bg-amber-500', label: status === 'paused' ? 'Paused' : 'Idle' };
  }
};

export const AgentFleetPage: React.FC = () => {
  const { selectedPortco, selectedPortcoName, portcos: registryPortcos } = usePortCo();
  const navigate = useNavigate();
  const [fleetFilter, setFleetFilter] = useState<string>('all');
  const [disabledKeys, setDisabledKeys] = useState<Set<string>>(new Set());
  const [entitledKeys, setEntitledKeys] = useState<Set<string>>(new Set());
  // Live per-tenant agent registry (from /agents/config) — the source of truth
  // for which agents exist for each business. Drives the fleet table instead of
  // the hardcoded demo list so newly onboarded firms appear automatically.
  const [liveConfigs, setLiveConfigs] = useState<any[]>([]);
  // Real per-(portco, UI-workstream) numbers from /api/fleet — replaces the
  // demo 'actionsToday'/'status' values with actual run history from Cosmos.
  const [realFleet, setRealFleet] = useState<Record<string, { actionsToday: number; status: 'running' | 'active' | 'idle'; lastRunAt: string }>>({});

  // Live per-tenant on/off state from /agents/config — an agent switched off
  // in the Agent Switches panel must never show as Active/Processing here.
  // Live workstream ids differ from UI ids in two cases (backend mapping):
  //   clinical_doc + patient_engagement -> UI 'clinical-ops'
  //   credentialing                    -> UI 'hr'
  // A UI agent shows Paused only when every underlying live workstream is off.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const configs = await rcmApi.getAgentConfigs(selectedPortco);
        if (cancelled) return;
        setLiveConfigs(configs);
        const disabled = new Set<string>();
        const entitled = new Set<string>();
        for (const c of configs) {
          entitled.add(`${c.portcoId}|${c.workstream}`);
          if (!c.enabled) disabled.add(`${c.portcoId}|${c.workstream}`);
        }
        setEntitledKeys(entitled);
        setDisabledKeys(disabled);
      } catch {
        if (!cancelled) { setDisabledKeys(new Set()); setEntitledKeys(new Set()); }
      }
      try {
        const res = await apiFetch(`${API_BASE}/fleet?portcoId=${encodeURIComponent(selectedPortco)}`);
        if (cancelled || !res.ok) return;
        const data = await res.json();
        const uiOf = (ws: string) =>
          ws === 'clinical_doc' || ws === 'patient_engagement' ? 'clinical-ops'
          : ws === 'credentialing' ? 'hr' : ws;
        const agg: Record<string, { actionsToday: number; status: 'running' | 'active' | 'idle'; lastRunAt: string }> = {};
        for (const a of (data?.agents ?? [])) {
          const key = `${a.portcoId}|${uiOf(a.workstream)}`;
          const cur = agg[key] || { actionsToday: 0, status: 'idle', lastRunAt: '' };
          cur.actionsToday += a.actionsToday || 0;
          cur.lastRunAt = (a.lastRunAt > cur.lastRunAt ? a.lastRunAt : cur.lastRunAt) || cur.lastRunAt;
          cur.status = (a.status === 'running' || cur.status === 'running') ? 'running'
            : (a.status === 'active' || cur.status === 'active') ? 'active' : 'idle';
          agg[key] = cur;
        }
        setRealFleet(agg);
      } catch {
        if (!cancelled) setRealFleet({});
      }
    };
    load();
    const t = setInterval(load, 30000); // keep in sync with the switch panel
    return () => { cancelled = true; clearInterval(t); };
  }, [selectedPortco]);

  const UI_TO_LIVE: Record<string, string[]> = {
    'clinical-ops': ['clinical_doc', 'patient_engagement'],
    hr: ['hr', 'credentialing'],
  };

  const displayStatus = (a: (typeof AGENTS)[number]): AgentStatus => {
    const type = (a as any).type || a.workstream;
    const liveIds = UI_TO_LIVE[type] ?? [type];
    const allOff = liveIds.every((id) => disabledKeys.has(`${a.portcoId}|${id}`));
    if (allOff) return 'paused';
    const real = realFleet[`${a.portcoId}|${type}`];
    if (real) {
      if (real.status === 'running') return 'processing';
      if (real.status === 'active') return 'active';
      return 'idle';
    }
    return a.status;
  };

  const displayLastActive = (a: (typeof AGENTS)[number]): string => {
    const type = (a as any).type || a.workstream;
    const real = realFleet[`${a.portcoId}|${type}`];
    if (real?.lastRunAt) {
      const mins = Math.round((Date.now() - new Date(real.lastRunAt).getTime()) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
      return `${Math.round(mins / 1440)}d ago`;
    }
    return a.lastActionAt;
  };

  const displayActionsToday = (a: (typeof AGENTS)[number]): number => {
    const type = (a as any).type || a.workstream;
    const real = realFleet[`${a.portcoId}|${type}`];
    return real ? real.actionsToday : (a.actionsToday || 0);
  };

  // Entitlement filter (deny-by-default): only purchased workstreams have config entries.
  // Live-first: when the config registry has loaded, build the fleet table from it
  // so every onboarded business (not just the demo fixtures) shows its agents.
  // Fall back to the hardcoded demo list only if the registry is unreachable.
  const TMPL_BY_TYPE = React.useMemo(
    () => new Map(AGENT_TEMPLATES.map((t) => [t.type, t])),
    []
  );
  const uiOf = (ws: string) =>
    ws === 'clinical_doc' || ws === 'patient_engagement' ? 'clinical-ops'
    : ws === 'credentialing' ? 'hr' : ws;
  const fleetAgents = React.useMemo<any[]>(() => {
    if (liveConfigs.length === 0) {
      return AGENTS.filter(
        (a) =>
          (selectedPortco === 'all' || a.portcoId === selectedPortco) &&
          entitledKeys.has(`${a.portcoId}|${a.workstream === 'clinical-ops' ? 'clinical_doc' : a.workstream}`)
      );
    }
    const byKey = new Map<string, any>();
    for (const c of liveConfigs) {
      if (selectedPortco !== 'all' && c.portcoId !== selectedPortco) continue;
      const ui = uiOf(c.workstream);
      const key = `${c.portcoId}|${ui}`;
      if (byKey.has(key)) continue;
      const tmpl = TMPL_BY_TYPE.get(ui);
      byKey.set(key, {
        ...(tmpl ?? { name: c.name, description: '', capabilities: [], kpis: [], recentRecommendations: [], version: 'v1', successRate: 0, actionsToday: 0, lastActionAt: '', workstream: ui, type: ui, status: 'idle' }),
        id: `agent_${ui}_${c.portcoId}`,
        portcoId: c.portcoId,
        type: ui,
        workstream: ui,
        name: tmpl?.name || c.name,
        // Live cards derive status/recency from realFleet + switch state — never
        // from demo fixtures. Idle is the honest baseline for never-run agents.
        status: 'idle',
        lastActionAt: '',
        actionsToday: 0,
        successRate: tmpl?.successRate ?? 0,
      });
    }
    return [...byKey.values()];
  }, [liveConfigs, selectedPortco, entitledKeys, TMPL_BY_TYPE]);
  const fleetDisplayAgents = fleetFilter === 'all'
    ? fleetAgents
    : fleetAgents.filter((a) => displayStatus(a) === fleetFilter);

  const filterPills = [
    { key: 'all', label: 'All', count: fleetAgents.length },
    { key: 'active', label: 'Running', count: fleetAgents.filter(a => displayStatus(a) === 'active').length },
    { key: 'processing', label: 'Processing', count: fleetAgents.filter(a => displayStatus(a) === 'processing').length },
    { key: 'idle', label: 'Idle', count: fleetAgents.filter(a => displayStatus(a) === 'idle').length },
    { key: 'paused', label: 'Paused', count: fleetAgents.filter(a => displayStatus(a) === 'paused').length },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Agent Fleet Monitor</h1>
        <p className="text-sm text-slate-500 mt-0.5">Real-time visibility across all deployed agents</p>
      </div>

      {/* Per-tenant agent on/off switches */}
      <AgentSwitchPanel />

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Agents</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{fleetAgents.length}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Across all PortCos</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Running</p>
          <p className="text-2xl font-bold text-mantis-600 mt-1">{fleetAgents.filter(a => ['active', 'processing'].includes(displayStatus(a))).length}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Active or processing</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Idle / Waiting</p>
          <p className="text-2xl font-bold text-amber-500 mt-1">{fleetAgents.filter(a => ['idle', 'paused'].includes(displayStatus(a))).length}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Awaiting triggers</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions Today</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{fleetAgents.reduce((sum, a) => sum + displayActionsToday(a), 0)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Automated executions</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Avg Success</p>
          <p className="text-2xl font-bold text-mantis-600 mt-1">
            {fleetAgents.length > 0 ? (fleetAgents.reduce((sum, a) => sum + (a.successRate || 0), 0) / fleetAgents.length).toFixed(1) : '0.0'}%
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Guardrail compliance</p>
        </div>
      </div>

      {/* Status Filter Pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {filterPills.map((pill) => (
          <button
            key={pill.key}
            onClick={() => setFleetFilter(pill.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
              fleetFilter === pill.key
                ? 'bg-mantis-600 text-white border-mantis-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
          >
            {pill.label} ({pill.count})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Agent Fleet Table */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Agent</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">PortCo</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Workstream</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions Today</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Success Rate</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {fleetDisplayAgents.map((agent) => {
                  const portco = registryPortcos.find(p => p.id === agent.portcoId);
                  const statusStyle = getStatusBadge(displayStatus(agent));
                  return (
                    <tr key={agent.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate('/workstreams')}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${statusStyle.dot}`} />
                          <div>
                            <p className="text-xs font-semibold text-slate-800">{agent.name}</p>
                            <p className="text-[10px] text-slate-400">{agent.version}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden sm:table-cell">
                        <span className="text-xs text-slate-600">{portco?.name || agent.portcoId}</span>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-xs text-slate-500 capitalize">{agent.workstream.replace('-', ' ')}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusStyle.bg}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-xs font-semibold text-slate-700">{displayActionsToday(agent)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right hidden lg:table-cell">
                        <span className="text-xs font-semibold text-mantis-600">{agent.successRate || 0}%</span>
                      </td>
                      <td className="px-4 py-2.5 text-right hidden lg:table-cell">
                        <span className="text-[10px] text-slate-400">{displayLastActive(agent)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {fleetDisplayAgents.length === 0 && (
            <div className="text-center py-8">
              <p className="text-xs text-slate-400">No agents match this filter</p>
            </div>
          )}
        </div>

        {/* Workstream Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Workstream Distribution</h3>
          <div className="space-y-2.5">
            {Object.entries(
              fleetAgents.reduce<Record<string, { count: number; actions: number; processing: number }>>((acc, a) => {
                const key = a.workstream;
                if (!acc[key]) acc[key] = { count: 0, actions: 0, processing: 0 };
                acc[key].count++;
                acc[key].actions += a.actionsToday || 0;
                if (a.status === 'processing' || a.status === 'active') acc[key].processing++;
                return acc;
              }, {} as Record<string, { count: number; actions: number; processing: number }>)
            ).map(([ws, data]) => (
              <div key={ws} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2.5">
                  <span className={`w-2 h-2 rounded-full ${data.processing > 0 ? 'bg-mantis-500' : 'bg-slate-300'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-700 capitalize">{ws.replace('-', ' ')}</p>
                    <p className="text-xs text-slate-500">{data.count} agent{data.count > 1 ? 's' : ''} • {data.actions} actions today</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                  data.processing > 0 ? 'bg-mantis-50 text-mantis-600 border-mantis-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                }`}>
                  {data.processing > 0 ? 'Running' : 'Idle'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
